import React, { useEffect, useState } from "react";
import { api, formatApiError, inr, fmtDateTime } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import {
  Robot, Check, X, Warning, WhatsappLogo, Play, Timer, Plus, Info
} from "@phosphor-icons/react";

const PRESET_DAYS = [1, 3, 7, 15, 30, 45, 60];

export default function ReminderAutomation() {
  const { activeCompanyId, activeStoreId } = useAuth();
  const [data, setData] = useState(null);
  const [twilioReady, setTwilioReady] = useState(false);
  const [waProvider, setWaProvider] = useState("");
  const [defaultTpl, setDefaultTpl] = useState("");
  const [days, setDays] = useState([3, 7, 15]);
  const [customDay, setCustomDay] = useState("");
  const [enabled, setEnabled] = useState(false);
  const [template, setTemplate] = useState("");
  const [saving, setSaving] = useState(false);
  const [running, setRunning] = useState(false);
  const [logs, setLogs] = useState([]);

  const load = async () => {
    const [{ data: cfg }, { data: lg }] = await Promise.all([
      api.get("/reminder-schedule"),
      api.get("/reminder-schedule/logs?limit=50"),
    ]);
    setData(cfg.schedule);
    setTwilioReady(cfg.twilio_configured);
    setWaProvider(cfg.whatsapp_provider || "");
    setDefaultTpl(cfg.default_template);
    setEnabled(!!cfg.schedule.enabled);
    setDays(cfg.schedule.trigger_days || []);
    setTemplate(cfg.schedule.template || cfg.default_template);
    setLogs(lg);
  };
  useEffect(() => { if (activeCompanyId) load(); }, [activeCompanyId, activeStoreId]);

  const toggleDay = (d) => {
    setDays((prev) => prev.includes(d) ? prev.filter((x) => x !== d) : [...prev, d].sort((a,b)=>a-b));
  };
  const addCustom = () => {
    const n = parseInt(customDay);
    if (!n || n <= 0) return;
    if (!days.includes(n)) setDays([...days, n].sort((a,b)=>a-b));
    setCustomDay("");
  };

  const save = async () => {
    if (days.length === 0) { toast.error("Select at least one trigger day"); return; }
    setSaving(true);
    try {
      await api.put("/reminder-schedule", { enabled, trigger_days: days, template });
      toast.success("Schedule saved");
      load();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setSaving(false); }
  };

  const runNow = async () => {
    if (!enabled) { toast.error("Enable automation first, or preview will fire nothing"); return; }
    setRunning(true);
    try {
      const { data } = await api.post("/reminder-schedule/run-now");
      const total = data.total || 0;
      toast.success(total > 0 ? `Fired ${total} reminder(s)` : "No reminders to fire right now");
      load();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setRunning(false); }
  };

  if (!data) return <div className="text-sm text-muted-foreground">Loading automation…</div>;

  return (
    <div className="space-y-6">
      {/* WhatsApp provider banner */}
      <div className={`card-flat p-4 flex items-start gap-3 ${twilioReady ? "" : ""}`}>
        <div className={`w-9 h-9 flex items-center justify-center rounded-md ${twilioReady ? "bg-success/10" : "bg-warning/10"}`}>
          {twilioReady
            ? <Check size={18} weight="bold" style={{ color: "hsl(var(--success))" }} />
            : <Warning size={18} weight="fill" style={{ color: "hsl(var(--warning))" }} />}
        </div>
        <div className="flex-1 min-w-0">
          <div className="font-heading font-semibold text-[14px] tracking-tight">
            {twilioReady
              ? `WhatsApp — Connected (${(waProvider || "provider").toUpperCase()})`
              : "WhatsApp — Not Configured"}
          </div>
          <div className="text-[12.5px] text-muted-foreground mt-0.5">
            {twilioReady
              ? `Reminders will be sent automatically via ${waProvider === "aisensy" ? "AiSensy" : "Twilio"} WhatsApp Business API.`
              : "Reminders will be recorded to the activity log below (no message sent). To enable real WhatsApp delivery, either (a) complete Twilio Trust Hub KYC or (b) sign up at aisensy.com and add AISENSY_API_KEY + AISENSY_CAMPAIGN_NAME to backend/.env."}
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Toggle + Trigger Days */}
        <div className="lg:col-span-2 card-flat p-6 space-y-6">
          <div className="flex items-start justify-between gap-4">
            <div>
              <div className="eyebrow flex items-center gap-1.5">
                <Robot size={12} weight="fill" /> Automation
              </div>
              <div className="font-heading text-lg font-semibold tracking-tight mt-0.5">
                Scheduled Payment Reminders
              </div>
              <div className="text-[12.5px] text-muted-foreground mt-1">
                Automatically fire WhatsApp reminders on selected days after an invoice becomes overdue. Covers both <b>Sales Invoices</b> and <b>Temp Invoices (Pending Payments)</b>. Runs hourly.
              </div>
            </div>
            <div className="flex items-center gap-2.5 shrink-0">
              <Label htmlFor="enabled-switch" className="text-[13px] font-medium">
                {enabled ? "Enabled" : "Disabled"}
              </Label>
              <Switch id="enabled-switch" checked={enabled} onCheckedChange={setEnabled} data-testid="automation-toggle" />
            </div>
          </div>

          <div>
            <div className="eyebrow mb-2">Trigger Days (after due date)</div>
            <div className="flex flex-wrap gap-2">
              {PRESET_DAYS.map((d) => {
                const active = days.includes(d);
                return (
                  <button
                    key={d}
                    onClick={() => toggleDay(d)}
                    type="button"
                    data-testid={`day-chip-${d}`}
                    className={`h-9 px-4 rounded-md border transition-colors text-[13px] font-medium font-mono ${
                      active
                        ? "bg-primary text-primary-foreground border-primary"
                        : "border-border hover:bg-secondary text-muted-foreground hover:text-foreground"
                    }`}
                  >
                    T+{d}
                  </button>
                );
              })}
              {days.filter((d) => !PRESET_DAYS.includes(d)).map((d) => (
                <button
                  key={d}
                  onClick={() => toggleDay(d)}
                  type="button"
                  className="h-9 px-4 rounded-md border border-primary bg-primary text-primary-foreground text-[13px] font-medium font-mono"
                >
                  T+{d} ×
                </button>
              ))}
            </div>
            <div className="mt-3 flex items-center gap-2">
              <Input
                type="number"
                value={customDay}
                onChange={(e) => setCustomDay(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), addCustom())}
                placeholder="Custom day (e.g., 5)"
                className="h-9 w-52 font-mono"
                data-testid="custom-day-input"
              />
              <Button variant="outline" onClick={addCustom} className="h-9" type="button" data-testid="custom-day-add">
                <Plus size={13} className="mr-1" /> Add
              </Button>
              <span className="text-[11px] text-muted-foreground ml-2">
                {days.length ? `${days.length} day(s) selected` : "No days selected"}
              </span>
            </div>
          </div>

          <div>
            <div className="eyebrow mb-2 flex items-center gap-1.5">
              Message Template
              <Info size={11} className="text-muted-foreground" />
            </div>
            <Textarea
              value={template}
              onChange={(e) => setTemplate(e.target.value)}
              rows={9}
              className="font-mono text-[12.5px]"
              data-testid="template-input"
            />
            <div className="text-[11px] text-muted-foreground mt-1.5">
              Placeholders:{" "}
              <code className="font-mono">{"{{customer_name}}"}</code>,{" "}
              <code className="font-mono">{"{{number}}"}</code>,{" "}
              <code className="font-mono">{"{{grand_total}}"}</code>,{" "}
              <code className="font-mono">{"{{balance_due}}"}</code>,{" "}
              <code className="font-mono">{"{{days_overdue}}"}</code>,{" "}
              <code className="font-mono">{"{{share_url}}"}</code>,{" "}
              <code className="font-mono">{"{{due_date}}"}</code>,{" "}
              <code className="font-mono">{"{{company_name}}"}</code>
            </div>
            <div className="flex justify-end mt-3">
              <Button variant="ghost" size="sm" className="h-8 text-[12px]" onClick={() => setTemplate(defaultTpl)}>
                Reset to default
              </Button>
            </div>
          </div>

          <div className="flex justify-end gap-2 border-t border-border pt-4">
            <Button variant="outline" onClick={runNow} disabled={running || saving} className="h-9" data-testid="run-now-btn">
              <Play size={13} className="mr-1.5" weight="fill" /> {running ? "Running…" : "Run Now"}
            </Button>
            <Button onClick={save} disabled={saving} className="h-9" data-testid="schedule-save-btn">
              {saving ? "Saving…" : "Save Schedule"}
            </Button>
          </div>
        </div>

        {/* Preview panel */}
        <div className="card-flat p-6">
          <div className="eyebrow">Preview</div>
          <div className="font-heading text-lg font-semibold tracking-tight mt-0.5">How it looks</div>
          <div className="mt-4 p-4 bg-[#e5f7dc] rounded-lg text-[12.5px] whitespace-pre-line font-mono leading-relaxed" style={{ color: "#111" }}>
            {template
              .replace("{{customer_name}}", "Acme Traders")
              .replace("{{number}}", "INV/2026/0001")
              .replace("{{grand_total}}", "₹2,360.00")
              .replace("{{balance_due}}", "₹1,360.00")
              .replace("{{days_overdue}}", "7")
              .replace("{{due_date}}", "2026-02-15")
              .replace("{{share_url}}", "https://…/p/invoice/…")
              .replace("{{company_name}}", "Vetrich Demo")}
          </div>
          <div className="text-[11px] text-muted-foreground mt-3 flex items-center gap-1.5">
            <WhatsappLogo size={12} weight="fill" style={{ color: "#25D366" }} />
            Rendered as WhatsApp message
          </div>
        </div>
      </div>

      {/* Activity log */}
      <div className="card-flat overflow-hidden">
        <div className="px-6 py-4 border-b border-border flex items-center justify-between">
          <div>
            <div className="eyebrow">Activity</div>
            <div className="font-heading text-lg font-semibold tracking-tight mt-0.5">Reminder Log</div>
          </div>
          <div className="text-[11px] text-muted-foreground">Last {logs.length} entries · Idempotent per (invoice, trigger day)</div>
        </div>
        <table className="data-table">
          <thead>
            <tr>
              <th>Fired At</th>
              <th>Source</th>
              <th>Invoice</th>
              <th>Customer</th>
              <th>Phone</th>
              <th className="num">Trigger</th>
              <th className="num">Overdue</th>
              <th className="num">Balance</th>
              <th>Channel</th>
              <th>Status</th>
            </tr>
          </thead>
          <tbody>
            {logs.length === 0 ? (
              <tr><td colSpan={10} className="text-center py-10 text-muted-foreground">No reminders fired yet.</td></tr>
            ) : logs.map((l) => (
              <tr key={l.id} data-testid={`log-row-${l.id}`}>
                <td className="font-mono text-[11.5px] text-muted-foreground">{fmtDateTime(l.fired_at)}</td>
                <td>
                  {l.source_type === "temp_invoice"
                    ? <span className="pill pill-warning">Temp Inv</span>
                    : <span className="pill pill-muted">Invoice</span>}
                </td>
                <td className="font-mono text-[12px]">{l.invoice_number}</td>
                <td>{l.customer_name}</td>
                <td className="font-mono text-[12px]">{l.customer_phone || "—"}</td>
                <td className="num"><span className="pill pill-info">T+{l.trigger_day}</span></td>
                <td className="num font-mono">{l.days_overdue}d</td>
                <td className="num font-mono">{inr(l.balance_due)}</td>
                <td>
                  {l.channel === "whatsapp"
                    ? <span className="pill pill-info flex items-center gap-1 w-fit"><WhatsappLogo size={10} weight="fill" /> WhatsApp</span>
                    : <span className="pill pill-muted">Log</span>}
                </td>
                <td>
                  {l.status === "sent" && <span className="pill pill-success">Sent</span>}
                  {l.status === "failed" && <span className="pill pill-danger" title={l.error || ""}>Failed</span>}
                  {l.status === "pending_manual" && <span className="pill pill-warning">Pending</span>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
